/**
 * apps/command/src/screens/FleetGrid.tsx — every kiosk in the province.
 *
 * ============================================================================
 * A TABLE, NOT A WALL OF CARDS (rebuilt 2026-08-18).
 * ============================================================================
 *
 * This was a responsive grid of tiles, three across. That layout has one
 * property worth naming: it looks identical whether the province runs three
 * machines or thirty-five, and at thirty-five it becomes a scroll nobody reads
 * to the bottom of. Comparison is the whole job of a fleet screen — which kiosk
 * has the least disk, which is slowest, which is behind on its version — and
 * cards make every comparison a scroll-and-remember exercise.
 *
 * So: filters that carry their counts, a distribution strip, and one dense
 * table sorted worst-first, with every column an operator would sort by.
 *
 * THE TILE COMPONENT IS NOT DELETED. `KioskTile` still serves the mahalla lead's
 * screen, where there are three kiosks and a card is the right density. This is
 * the city-wide view and it is a different question.
 */
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { KIOSK_STATUSES, type KioskStatus } from '@navoiy/domain';
import { useSession } from '../auth/SessionContext.js';
import { t } from '../i18n/index.js';
import { ageLabel, integer } from '../lib/format.js';
import { statusOf, useFleet, type MbtKiosk } from '../realtime/useFleet.js';
import { EmptyState, Notice, Panel, PanelHeader, Skeleton, StatusPill } from '../ui/index.jsx';
import { PageHeader, KpiCard, TabStrip } from '../ui/console.jsx';
import { BarChart } from '../ui/charts.jsx';
import { DataTable, LiveBadge, ProgressBar, SegmentedControl, type Column } from '../ui/data.jsx';

type Sort = 'risk' | 'disk' | 'queue' | 'appeals' | 'name';

/**
 * The row's left-edge colour, per status.
 *
 * A BORDER class rather than a tone name, because `DataTable` puts it straight
 * on a cell — and because §15.2's status colours are reserved: `crit` here
 * means a machine is broken, exactly as it does on every other screen.
 */
/**
 * The five statuses as BACKGROUND colours — the card's top bar and the table
 * row's left edge, which are the same device in two shapes.
 *
 * ONE MAP, and background rather than border, after two separate bugs said so:
 *
 *  · The card bar first reused a border map and painted with
 *    `backgroundColor: 'currentColor'`. `border-crit` sets `border-color`;
 *    `currentColor` is the element's TEXT colour, which on a card is
 *    `ink-100`. Every bar came out the same near-black — a strip whose whole
 *    job is to say «this one is broken» saying nothing, on all four cards,
 *    while looking deliberate.
 *
 *  · The table accent first went on the `<td>` as `border-crit`, beside the
 *    cell's own `border-grid`. Both are `border-color` at equal specificity,
 *    so the winner came down to stylesheet order — and it went different ways
 *    for different colours. The CRITICAL row drew its accent in hairline grey
 *    while the offline rows drew theirs correctly.
 *
 * A background colour on an element of its own collides with nothing.
 */
const BAR_OF: Record<string, string> = {
  healthy: 'bg-ok',
  warning: 'bg-warn',
  critical: 'bg-crit',
  offline: 'bg-offline',
  maintenance: 'bg-info',
};

export function FleetGrid(): React.JSX.Element {
  const { expire } = useSession();
  const onExpired = useCallback(() => expire(), [expire]);
  const fleet = useFleet(onExpired);
  const [filter, setFilter] = useState<KioskStatus | 'all'>('all');
  const [sort, setSort] = useState<Sort>('risk');
  /**
   * CARDS BY DEFAULT (owner directive, 2026-08-19). The table is one click away.
   *
   * Both forms are the right answer to a different question, and the default
   * decides which question the screen is understood to be asking.
   *
   * Cards are for TRIAGE: on a wall display across a room nobody reads an
   * eleven-column table, they want to see four machines and know which one is
   * red. That is what somebody opening «Kiosklar» is nearly always doing, and
   * it is the reading the rest of this console is built for.
   *
   * The table is for LOOKUP — eleven columns, sortable, totals at the foot, and
   * the only form in which «which kiosk has the fullest disk» is one glance
   * down a column. It is kept, not removed: it is the better tool for the
   * narrower job, and deleting it would trade a real capability for a default.
   */
  const [view, setView] = useState<'table' | 'cards'>('cards');

  const byStatus = useMemo(() => {
    const map = new Map<KioskStatus, number>(KIOSK_STATUSES.map((status) => [status, 0]));
    for (const kiosk of fleet.kiosks) {
      const status = statusOf(kiosk, fleet.now);
      map.set(status, (map.get(status) ?? 0) + 1);
    }
    return map;
  }, [fleet.kiosks, fleet.now]);

  const RANK: Record<string, number> = {
    critical: 0,
    offline: 1,
    warning: 2,
    maintenance: 3,
    healthy: 4,
  };

  const rows = useMemo(() => {
    const list = fleet.kiosks.filter(
      (kiosk) => filter === 'all' || statusOf(kiosk, fleet.now) === filter,
    );
    const disk = (k: MbtKiosk): number => k.telemetry?.health?.disk_free_pct ?? 101;
    const queue = (k: MbtKiosk): number => k.telemetry?.work?.queue_depth ?? 0;
    const appeals = (k: MbtKiosk): number => k.telemetry?.work?.appeals_today ?? 0;
    return [...list].sort((a, b) => {
      if (sort === 'disk') return disk(a) - disk(b);
      if (sort === 'queue') return queue(b) - queue(a);
      if (sort === 'appeals') return appeals(b) - appeals(a);
      if (sort === 'name') return a.short_code.localeCompare(b.short_code);
      /* Default: worst first. Nobody opens a fleet screen alphabetically. */
      return (
        (RANK[statusOf(a, fleet.now)] ?? 9) - (RANK[statusOf(b, fleet.now)] ?? 9) ||
        disk(a) - disk(b)
      );
    });
  }, [fleet.kiosks, fleet.now, filter, sort]);

  if (fleet.error === 'forbidden') return <Notice tone="crit">{t('error.forbidden')}</Notice>;

  const queueTotal = fleet.kiosks.reduce((sum, k) => sum + (k.telemetry?.work?.queue_depth ?? 0), 0);
  const appealsTotal = fleet.kiosks.reduce(
    (sum, k) => sum + (k.telemetry?.work?.appeals_today ?? 0),
    0,
  );
  const enrolled = fleet.kiosks.filter((k) => k.enrolled).length;
  /**
   * Version spread, as a chart rather than a sentence.
   *
   * «2 ta versiya» tells an operator there is a problem; this tells them WHICH
   * build is behind and on how many machines, which is the thing they act on.
   */
  const versionBars = useMemo(() => {
    const counts = new Map<string, number>();
    for (const kiosk of fleet.kiosks) {
      const version = kiosk.app_version ?? '—';
      counts.set(version, (counts.get(version) ?? 0) + 1);
    }
    const newest = [...counts.keys()].sort().at(-1);
    return [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([version, count]) => ({
        key: version,
        label: version,
        value: count,
        /* The NEWEST build is the highlight — the bars beside it are the fleet
           that has not taken it yet, which is the reading that matters. */
        highlight: version === newest,
      }));
  }, [fleet.kiosks]);

  const columns: Column<MbtKiosk>[] = [
    {
      key: 'kiosk',
      label: t('console.colKiosk'),
      render: (kiosk) => (
        <Link to={`/fleet/${kiosk.id}`} className="block min-w-0">
          <p className="truncate font-semibold text-ink-100">{kiosk.short_code}</p>
          <p className="truncate text-micro text-ink-500">{kiosk.label}</p>
        </Link>
      ),
    },
    {
      key: 'mahalla',
      label: t('overview.colMahalla'),
      render: (kiosk) => <span className="text-ink-300">{kiosk.mahalla_name}</span>,
    },
    {
      key: 'status',
      label: t('console.colStatus'),
      render: (kiosk) => {
        const status = statusOf(kiosk, fleet.now);
        return <StatusPill status={status} label={t(`status.${status}`)} />;
      },
    },
    {
      key: 'alerts',
      label: t('console.colAlerts'),
      numeric: true,
      render: (kiosk) =>
        (kiosk.open_alerts ?? 0) === 0 ? (
          <span className="text-ink-500">0</span>
        ) : (
          <span
            className={
              (kiosk.open_alerts_critical ?? 0) > 0 ? 'font-semibold text-crit' : 'text-warn'
            }
          >
            {kiosk.open_alerts}
          </span>
        ),
    },
    {
      key: 'appeals',
      label: t('console.colAppeals'),
      numeric: true,
      render: (kiosk) => {
        const value = kiosk.telemetry?.work?.appeals_today;
        return typeof value === 'number' ? (
          <span className="text-ok">{value}</span>
        ) : (
          <span className="text-ink-500">—</span>
        );
      },
    },
    {
      key: 'queue',
      label: t('console.colQueue'),
      numeric: true,
      render: (kiosk) => {
        const value = kiosk.telemetry?.work?.queue_depth;
        if (typeof value !== 'number') return <span className="text-ink-500">—</span>;
        return <span className={value > 0 ? 'font-semibold text-warn' : 'text-ink-300'}>{value}</span>;
      },
    },
    {
      key: 'disk',
      label: t('fleet.metric.disk'),
      render: (kiosk) => {
        const free = kiosk.telemetry?.health?.disk_free_pct;
        if (typeof free !== 'number') return <span className="text-ink-500">—</span>;
        return (
          <div className="flex items-center gap-2">
            <ProgressBar pct={100 - free} tone={free < 10 ? 'crit' : free < 20 ? 'warn' : 'ok'} />
            <span className="w-10 shrink-0 text-right tabular-nums text-ink-300">
              {Math.round(free)}%
            </span>
          </div>
        );
      },
      width: '10rem',
    },
    {
      key: 'temp',
      label: t('fleet.metric.temp'),
      numeric: true,
      render: (kiosk) => {
        const temp = kiosk.telemetry?.health?.cpu_temp_c;
        if (typeof temp !== 'number') return <span className="text-ink-500">—</span>;
        return <span className={temp > 70 ? 'text-warn' : 'text-ink-300'}>{Math.round(temp)}°</span>;
      },
    },
    {
      key: 'rtt',
      label: t('fleet.metric.rtt'),
      numeric: true,
      render: (kiosk) => {
        const rtt = kiosk.telemetry?.network?.rtt_ms;
        if (typeof rtt !== 'number') return <span className="text-ink-500">—</span>;
        return <span className={rtt > 400 ? 'text-warn' : 'text-ink-300'}>{rtt}</span>;
      },
    },
    {
      key: 'version',
      label: t('fleet.metric.version'),
      render: (kiosk) =>
        kiosk.app_version ? (
          <span className="tabular-nums text-ink-300">{kiosk.app_version}</span>
        ) : (
          <span className="text-ink-500">—</span>
        ),
    },
    {
      key: 'seen',
      label: t('console.colSeen'),
      numeric: true,
      render: (kiosk) =>
        kiosk.telemetry_at ? (
          <span className="text-ink-500">{ageLabel(new Date(kiosk.telemetry_at), fleet.now, t)}</span>
        ) : (
          <span className="text-ink-500">{t('app.never')}</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        eyebrow={
          <>
            {fleet.live ? <LiveBadge label={t('status.live')} /> : null}
            <span>{t('fleet.eyebrow')}</span>
          </>
        }
        title={t('nav.fleet')}
        subtitle={t('fleet.subtitle', { count: fleet.kiosks.length })}
        right={
          fleet.fetchedAt === null ? undefined : (
            <span className="text-micro tabular-nums text-ink-500">
              {t('app.asOf', { at: ageLabel(fleet.fetchedAt, fleet.now, t) })}
            </span>
          )
        }
      />

      {fleet.error === 'network' ? <Notice tone="warn">{t('error.network')}</Notice> : null}
      {fleet.scoped ? <Notice tone="quiet">{t('fleet.scopedNote')}</Notice> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('fleet.enrolled')}
          value={`${enrolled}/${fleet.kiosks.length}`}
          absent={t('app.notYet')}
          tone={enrolled === fleet.kiosks.length ? 'ok' : 'warn'}
          footerLabel={t('status.offline')}
          footerValue={String(byStatus.get('offline') ?? 0)}
        />
        <KpiCard
          label={t('console.colAppeals')}
          value={integer(appealsTotal)}
          absent={t('app.notYet')}
          spread={fleet.kiosks.map((k) => k.telemetry?.work?.appeals_today ?? 0)}
          tone="ok"
          footerLabel={t('fleet.perKioskAvg')}
          footerValue={
            fleet.kiosks.length === 0 ? '—' : (appealsTotal / fleet.kiosks.length).toFixed(1)
          }
        />
        <KpiCard
          label={t('overview.queueTotal')}
          value={integer(queueTotal)}
          absent={t('app.notYet')}
          spread={fleet.kiosks.map((k) => k.telemetry?.work?.queue_depth ?? 0)}
          tone={queueTotal > 0 ? 'warn' : 'ok'}
          footerLabel={t('fleet.kiosksWithQueue')}
          footerValue={String(
            fleet.kiosks.filter((k) => (k.telemetry?.work?.queue_depth ?? 0) > 0).length,
          )}
        />
        <KpiCard
          label={t('overview.openAlerts')}
          value={integer(fleet.kiosks.reduce((sum, k) => sum + (k.open_alerts ?? 0), 0))}
          absent={t('app.notYet')}
          tone={(byStatus.get('critical') ?? 0) > 0 ? 'crit' : 'ok'}
          footerLabel={t('status.critical')}
          footerValue={String(byStatus.get('critical') ?? 0)}
        />
      </div>

      <Panel>
        <PanelHeader title={t('fleet.versionSpread')} aside={t('fleet.versionSpreadHint')} />
        <div className="px-4 py-4">
          <BarChart bars={versionBars} />
        </div>
      </Panel>

      <Panel>
        <PanelHeader
          title={t('nav.fleet')}
          aside={
            <div className="flex flex-wrap items-center gap-3">
              <SegmentedControl
                value={sort}
                onChange={setSort}
                options={[
                  { value: 'risk', label: t('overview.sortRisk') },
                  { value: 'disk', label: t('fleet.metric.disk') },
                  { value: 'queue', label: t('console.colQueue') },
                  { value: 'appeals', label: t('console.colAppeals') },
                  { value: 'name', label: t('overview.sortName') },
                ]}
              />
              <SegmentedControl
                value={view}
                onChange={setView}
                options={[
                  { value: 'table' as const, label: t('fleet.viewTable') },
                  { value: 'cards' as const, label: t('fleet.viewCards') },
                ]}
              />
            </div>
          }
        />
        <div className="border-b border-hairline px-4 py-3">
          <TabStrip
            value={filter}
            onChange={setFilter}
            tabs={[
              { value: 'all' as const, label: t('fleet.filterAll'), count: fleet.kiosks.length },
              ...KIOSK_STATUSES.map((status) => ({
                value: status,
                label: t(`status.${status}`),
                count: byStatus.get(status) ?? 0,
              })),
            ]}
          />
        </div>
        {fleet.loading ? (
          <div className="px-4 py-4">
            <Skeleton className="h-64" />
          </div>
        ) : fleet.kiosks.length === 0 ? (
          /* No kiosks at all is a different sentence from none in this filter —
             the first means the province has no machines enrolled, the second
             means look somewhere else. */
          <EmptyState message={t('fleet.empty')} />
        ) : rows.length === 0 ? (
          <EmptyState message={t('fleet.emptyFiltered')} />
        ) : view === 'cards' ? (
          <div className="grid gap-4 p-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {rows.map((kiosk) => (
              <KioskCard key={kiosk.id} kiosk={kiosk} status={statusOf(kiosk, fleet.now)} now={fleet.now} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={rows}
            keyOf={(kiosk) => kiosk.id}
            /* The status, as a bar at the start of the row. With eleven columns
               the pill is one cell among many and finding the two broken
               machines means reading down a column; this makes it a glance. */
            rowAccent={(kiosk) => BAR_OF[statusOf(kiosk, fleet.now)] ?? null}
            total={[
              t('overview.totalRow'),
              '',
              '',
              integer(rows.reduce((sum, k) => sum + (k.open_alerts ?? 0), 0)),
              integer(rows.reduce((sum, k) => sum + (k.telemetry?.work?.appeals_today ?? 0), 0)),
              integer(rows.reduce((sum, k) => sum + (k.telemetry?.work?.queue_depth ?? 0), 0)),
              '',
              '',
              '',
              '',
              '',
            ]}
          />
        )}
      </Panel>
    </div>
  );
}

/**
 * One kiosk as a card — the triage view.
 *
 * ============================================================================
 * IT SHOWS FEWER FACTS THAN THE TABLE ROW, ON PURPOSE.
 * ============================================================================
 *
 * The table carries eleven columns because an operator looking something up
 * needs all of them. A card is read across a room, and a card with eleven
 * numbers on it is a table row with a border. So this carries the four an
 * operator triages on — alerts, disk, queue, appeals today — and the identity
 * big enough to read from three metres.
 *
 * The whole card is a link. On a wall display the click target is a poster,
 * not a nine-pixel short code in the first column.
 */
function KioskCard({
  kiosk,
  status,
  now,
}: {
  readonly kiosk: MbtKiosk;
  readonly status: KioskStatus;
  readonly now: Date;
}): React.JSX.Element {
  const disk = kiosk.telemetry?.health?.disk_free_pct;
  const alerts = kiosk.open_alerts ?? 0;

  return (
    <Link
      to={`/fleet/${kiosk.id}`}
      className="flex flex-col gap-3 overflow-hidden rounded-card border border-hairline bg-panel bg-card p-3 shadow-card transition-shadow duration-state hover:shadow-lift"
    >
      {/* The status as a full-width bar at the top of the card, the same role
          the left border plays in the table: colour first, words second. */}
      <span
        className={`-mx-3 -mt-3 h-1.5 ${BAR_OF[status] ?? 'bg-hairline'}`}
        aria-hidden="true"
      />

      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {/* `figure`, not `lead`. This is the default view now, and the short
              code is the one string somebody reads from across a room to know
              which machine a card is — it should be the largest thing on it. */}
          <p className="truncate text-figure font-bold tabular-nums leading-none text-ink-100">
            {kiosk.short_code}
          </p>
          <p className="mt-1 truncate text-micro text-ink-500">{kiosk.label}</p>
          <p className="truncate text-micro text-ink-500">{kiosk.mahalla_name}</p>
        </div>
        <StatusPill status={status} label={t(`status.${status}`)} />
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-2">
        <div className="min-w-0">
          <p className="truncate text-nano uppercase text-ink-500">{t('console.colAlerts')}</p>
          <p
            className={`truncate text-label font-semibold tabular-nums ${
              alerts > 0 ? 'text-crit' : 'text-ink-100'
            }`}
          >
            {alerts}
          </p>
        </div>
        <div className="min-w-0">
          <p className="truncate text-nano uppercase text-ink-500">{t('fleet.metric.disk')}</p>
          {/* «nomaʼlum» rather than 0 — §3.5's offline truth. A kiosk that has
              not reported has no disk reading, and printing a confident 0%
              would raise §4.4's critical threshold on a healthy machine. */}
          <p className="truncate text-label font-semibold tabular-nums text-ink-100">
            {typeof disk === 'number' ? `${Math.round(disk)}%` : t('app.unknown')}
          </p>
        </div>
        <div className="min-w-0">
          <p className="truncate text-nano uppercase text-ink-500">{t('console.colQueue')}</p>
          <p className="truncate text-label font-semibold tabular-nums text-ink-100">
            {kiosk.telemetry?.work?.queue_depth ?? '—'}
          </p>
        </div>
        <div className="min-w-0">
          <p className="truncate text-nano uppercase text-ink-500">{t('console.colAppeals')}</p>
          <p className="truncate text-label font-semibold tabular-nums text-ink-100">
            {kiosk.telemetry?.work?.appeals_today ?? '—'}
          </p>
        </div>
      </div>

      {typeof disk === 'number' ? (
        <ProgressBar pct={disk} tone={disk < 20 ? 'crit' : disk < 40 ? 'warn' : 'ok'} />
      ) : null}

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-hairline pt-2 text-micro text-ink-500">
        <span className="truncate">{kiosk.app_version ?? t('app.unknown')}</span>
        <span className="shrink-0 tabular-nums">
          {kiosk.telemetry_at ? ageLabel(new Date(kiosk.telemetry_at), now, t) : t('app.never')}
        </span>
      </div>
    </Link>
  );
}
